import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Trash2 } from "lucide-react";
import { Toast } from "../../components/Toast";

type RegexFlags = {
  global: boolean;
  ignoreCase: boolean;
  multiline: boolean;
  dotAll: boolean;
  unicode: boolean;
  sticky: boolean;
};

type MatchResult = {
  match: string;
  index: number;
  groups: (string | undefined)[];
  namedGroups?: Record<string, string | undefined>;
};

type RegexTestResult =
  | { status: "empty" }
  | { status: "valid"; matches: MatchResult[]; matchCount: number }
  | { status: "invalid"; error: string };

const buildFlagsString = (flags: RegexFlags): string => {
  let result = "";
  if (flags.global) result += "g";
  if (flags.ignoreCase) result += "i";
  if (flags.multiline) result += "m";
  if (flags.dotAll) result += "s";
  if (flags.unicode) result += "u";
  if (flags.sticky) result += "y";
  return result;
};

const testRegex = (
  pattern: string,
  text: string,
  flags: RegexFlags,
): RegexTestResult => {
  const trimmedPattern = pattern.trim();
  const trimmedText = text;

  if (!trimmedPattern) {
    return { status: "empty" };
  }

  try {
    const flagsString = buildFlagsString(flags);
    const regex = new RegExp(trimmedPattern, flagsString);

    if (!flags.global) {
      const match = regex.exec(trimmedText);
      if (!match) {
        return { status: "valid", matches: [], matchCount: 0 };
      }

      const groups: (string | undefined)[] = [];
      for (let i = 0; i < match.length; i++) {
        groups.push(match[i]);
      }

      const result: MatchResult = {
        match: match[0],
        index: match.index,
        groups,
      };

      if (match.groups) {
        result.namedGroups = match.groups;
      }

      return { status: "valid", matches: [result], matchCount: 1 };
    }

    const matches: MatchResult[] = [];
    let match;
    const globalRegex = new RegExp(trimmedPattern, flagsString);

    while ((match = globalRegex.exec(trimmedText)) !== null) {
      const groups: (string | undefined)[] = [];
      for (let i = 0; i < match.length; i++) {
        groups.push(match[i]);
      }

      const result: MatchResult = {
        match: match[0],
        index: match.index,
        groups,
      };

      if (match.groups) {
        result.namedGroups = match.groups;
      }

      matches.push(result);

      if (match[0] === "") {
        globalRegex.lastIndex++;
      }
    }

    return { status: "valid", matches, matchCount: matches.length };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { status: "invalid", error: errorMessage };
  }
};

export function RegexTester() {
  const { t } = useTranslation();
  const [pattern, setPattern] = useState("");
  const [text, setText] = useState("");
  const [flags, setFlags] = useState<RegexFlags>({
    global: true,
    ignoreCase: false,
    multiline: false,
    dotAll: false,
    unicode: false,
    sticky: false,
  });
  const [result, setResult] = useState<RegexTestResult>({ status: "empty" });
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    setResult(testRegex(pattern, text, flags));
  }, [pattern, text, flags]);

  const toggleFlag = useCallback((flag: keyof RegexFlags) => {
    setFlags((prev) => ({ ...prev, [flag]: !prev[flag] }));
  }, []);

  const clearAll = () => {
    setPattern("");
    setText("");
    setResult({ status: "empty" });
  };

  const copyPattern = async () => {
    if (!pattern) return;
    try {
      const flagsString = buildFlagsString(flags);
      const patternWithFlags = `/${pattern}/${flagsString}`;
      await navigator.clipboard.writeText(patternWithFlags);
      setToastMessage(t("regexTester.copied"));
    } catch {
      setToastMessage(t("regexTester.copyFailed"));
    }
  };

  const renderHighlightedText = () => {
    if (result.status !== "valid" || result.matches.length === 0 || !text) {
      return null;
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let keyCounter = 0;

    for (const match of result.matches) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${keyCounter++}`}>
            {text.slice(lastIndex, match.index)}
          </span>,
        );
      }

      parts.push(
        <mark key={`match-${keyCounter++}`} className="regex-match-highlight">
          {match.match}
        </mark>,
      );

      lastIndex = match.index + match.match.length;
    }

    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${keyCounter++}`}>{text.slice(lastIndex)}</span>,
      );
    }

    return parts;
  };

  const renderMatchDetails = () => {
    if (result.status !== "valid") return null;

    if (result.matches.length === 0) {
      return (
        <div className="regex-no-matches">
          <p>{t("regexTester.noMatches")}</p>
        </div>
      );
    }

    return (
      <div className="regex-matches-list">
        <h4>
          {t("regexTester.matches")} ({result.matchCount})
        </h4>
        {result.matches.map((match, index) => (
          <div key={index} className="regex-match-item">
            <div className="regex-match-header">
              <span className="regex-match-number">#{index + 1}</span>
              <span className="regex-match-position">
                {t("regexTester.position", { index: match.index })}
              </span>
            </div>
            <div className="regex-match-content">
              <code>{match.match}</code>
            </div>
            {match.groups.length > 1 && (
              <div className="regex-match-groups">
                <p>{t("regexTester.groups")}</p>
                <ul>
                  {match.groups.slice(1).map((group, groupIndex) => (
                    <li key={groupIndex}>
                      <span className="regex-group-index">
                        {groupIndex + 1}:
                      </span>
                      <code>{group ?? "undefined"}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {match.namedGroups && Object.keys(match.namedGroups).length > 0 && (
              <div className="regex-match-named-groups">
                <p>{t("regexTester.namedGroups")}</p>
                <ul>
                  {Object.entries(match.namedGroups).map(([name, value]) => (
                    <li key={name}>
                      <span className="regex-group-name">{name}:</span>
                      <code>{value ?? "undefined"}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStatus = () => {
    if (result.status === "empty") {
      return (
        <p className="regex-status regex-status-empty">
          {t("regexTester.enterPattern")}
        </p>
      );
    }
    if (result.status === "invalid") {
      return (
        <p className="regex-status regex-status-error">
          {t("regexTester.invalid")}: {result.error}
        </p>
      );
    }
    if (result.matches.length === 0) {
      return (
        <p className="regex-status regex-status-no-match">
          {t("regexTester.noMatchesFound")}
        </p>
      );
    }
    return (
      <p className="regex-status regex-status-success">
        {t("regexTester.matchesFound", { count: result.matchCount })}
      </p>
    );
  };

  const flagOptions: {
    key: keyof RegexFlags;
    label: string;
    description: string;
  }[] = [
    { key: "global", label: "g", description: t("regexTester.flags.global") },
    {
      key: "ignoreCase",
      label: "i",
      description: t("regexTester.flags.ignoreCase"),
    },
    {
      key: "multiline",
      label: "m",
      description: t("regexTester.flags.multiline"),
    },
    { key: "dotAll", label: "s", description: t("regexTester.flags.dotAll") },
    { key: "unicode", label: "u", description: t("regexTester.flags.unicode") },
    { key: "sticky", label: "y", description: t("regexTester.flags.sticky") },
  ];

  return (
    <div className="regex-tester">
      <div className="regex-grid">
        <div className="regex-card">
          <div className="regex-pattern-section">
            <label className="regex-label" htmlFor="regex-pattern">
              {t("regexTester.patternLabel")}
            </label>
            <div className="regex-pattern-wrapper">
              <span className="regex-delimiter">/</span>
              <input
                id="regex-pattern"
                type="text"
                className="regex-pattern-input"
                placeholder={t("regexTester.patternPlaceholder")}
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
              />
              <span className="regex-delimiter">/</span>
              <span className="regex-flags-display">
                {buildFlagsString(flags)}
              </span>
            </div>
          </div>

          <div className="regex-flags-section">
            <label className="regex-label">{t("regexTester.flagsLabel")}</label>
            <div className="regex-flags-grid">
              {flagOptions.map(({ key, label, description }) => (
                <button
                  key={key}
                  type="button"
                  className={`regex-flag-btn ${flags[key] ? "active" : ""}`}
                  onClick={() => toggleFlag(key)}
                  title={description}
                >
                  <span className="regex-flag-letter">{label}</span>
                  <span className="regex-flag-desc">{description}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="regex-text-section">
            <label className="regex-label" htmlFor="regex-text">
              {t("regexTester.textLabel")}
            </label>
            <textarea
              id="regex-text"
              className="regex-textarea"
              placeholder={t("regexTester.textPlaceholder")}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>

          <div className="regex-actions">
            <button
              type="button"
              className="regex-secondary-btn"
              onClick={copyPattern}
              disabled={!pattern}
            >
              <Copy size={16} />
              {t("regexTester.copy")}
            </button>
            <button
              type="button"
              className="regex-ghost-btn"
              onClick={clearAll}
              disabled={!pattern && !text}
            >
              <Trash2 size={16} />
              {t("regexTester.clear")}
            </button>
          </div>

          {renderStatus()}
        </div>

        <div className="regex-card">
          <label className="regex-label">{t("regexTester.resultsLabel")}</label>

          {text && result.status === "valid" && result.matches.length > 0 && (
            <div className="regex-highlighted-text">
              <pre>{renderHighlightedText()}</pre>
            </div>
          )}

          {renderMatchDetails()}

          {!text && (
            <div className="regex-empty-state">
              <p>{t("regexTester.emptyState")}</p>
            </div>
          )}
        </div>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
